import React, { CSSProperties, forwardRef, ReactNode } from 'react'
import type {
  ComponentConfig,
  DefaultComponentProps,
  ObjectField,
} from '@puckeditor/core'

import { getClassNameFactory } from '../../utils/get-class-name-factory'
import * as styles from './Layout.module.scss'
import { SectionConfigContext } from '../Section'
import { Spacing, defaultSpacing, SpacingGrid } from '../../fields/SpacingField'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  ShadowProps,
  backgroundStyleField,
  borderStyleField,
  shadowStyleField,
  splitBackgroundLayers,
  applyBorderStyle,
  applyShadowStyle,
} from '../../fields/StyleFields'

const getClassName = getClassNameFactory('Layout', styles)

type LayoutFieldProps = {
  anchorId?: string
  padding?: Spacing
  margin?: Spacing
  spanCol?: number
  spanRow?: number
  grow?: boolean
  mobileOrder?: number
  width?: string
  height?: string
  minWidth?: string
  minHeight?: string
  maxWidth?: string
  blockMaxWidth?: string
  position?: '' | 'relative'
  zIndex?: number
  centered?: boolean
  alignSelf?: 'auto' | 'start' | 'center' | 'end' | 'stretch'
}

export type WithLayout<Props extends DefaultComponentProps> = Props & {
  layout?: LayoutFieldProps
}

type LayoutProps = WithLayout<{
  children: ReactNode
  className?: string
  style?: CSSProperties
}>

/**
 * Sanitize an anchor id for use as an HTML id: spaces become dashes, anything
 * outside [a-zA-Z0-9_-] is stripped. Uniqueness is the editor author's
 * responsibility — duplicate ids make in-page anchors jump to the first match.
 */
export const sanitizeAnchorId = (v: string | undefined): string | undefined => {
  if (!v) return undefined
  const clean = v.trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9_-]/g, '')
  return clean || undefined
}

/** Return the CSS value or undefined if empty/zero. Auto-appends px to bare numbers. */
const cssOrUndefined = (v: string | undefined) => {
  if (!v || v === '0') return undefined
  // If the value is a bare number (e.g. "10"), append px
  if (/^\d+(\.\d+)?$/.test(v)) return `${v}px`
  return v
}

export const layoutField: ObjectField<LayoutFieldProps> = {
  type: 'object',
  objectFields: {
    anchorId: {
      label: 'Anchor ID (link to it with #the-id)',
      type: 'text',
      placeholder: 'signup-section',
    },
    spanCol: {
      label: 'Grid Columns',
      type: 'number',
      min: 1,
      max: 12,
    },
    spanRow: {
      label: 'Grid Rows',
      type: 'number',
      min: 1,
      max: 12,
    },
    grow: {
      label: 'Flex Grow',
      type: 'radio',
      options: [
        { label: 'true', value: true },
        { label: 'false', value: false },
      ],
    },
    padding: {
      type: 'custom',
      label: 'Padding',
      render: ({ value, onChange }) => (
        <SpacingGrid
          title="Padding"
          value={typeof value === 'object' && value ? value : defaultSpacing}
          onChange={onChange}
        />
      ),
    },
    margin: {
      type: 'custom',
      label: 'Margin',
      render: ({ value, onChange }) => (
        <SpacingGrid
          title="Margin"
          value={typeof value === 'object' && value ? value : defaultSpacing}
          onChange={onChange}
        />
      ),
    },
    mobileOrder: {
      label: 'Mobile Order',
      type: 'number',
      min: 0,
    },
    width: {
      label: 'Width',
      type: 'text',
      placeholder: 'e.g. 500px, 50%',
    },
    height: {
      label: 'Height',
      type: 'text',
      placeholder: 'e.g. 300px, auto',
    },
    minWidth: {
      label: 'Min Width',
      type: 'text',
      placeholder: 'e.g. 200px',
    },
    minHeight: {
      label: 'Min Height',
      type: 'text',
      placeholder: 'e.g. 100px',
    },
    blockMaxWidth: {
      label: 'Max Width (shrinks on smaller screens)',
      type: 'text',
      placeholder: '650px',
    },
    maxWidth: {
      label: 'Inner Content Max Width',
      type: 'text',
      placeholder: '1280px',
    },
    position: {
      label: 'Position',
      type: 'radio',
      options: [
        { label: 'Default', value: '' },
        { label: 'Relative', value: 'relative' },
      ],
    },
    zIndex: {
      label: 'Z-Index',
      type: 'number',
    },
    centered: {
      label: 'Centered',
      type: 'radio',
      options: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
    },
    alignSelf: {
      label: 'Self Alignment',
      type: 'select',
      options: [
        { label: 'Auto', value: 'auto' },
        { label: 'Start', value: 'start' },
        { label: 'Center', value: 'center' },
        { label: 'End', value: 'end' },
        { label: 'Stretch', value: 'stretch' },
      ],
    },
  },
}

const Layout = forwardRef<HTMLDivElement, LayoutProps>(
  ({ children, className, layout, style }, ref) => {
    const padding = layout?.padding
    const margin = layout?.margin

    return (
      <div
        id={sanitizeAnchorId(layout?.anchorId)}
        className={className}
        style={{
          gridColumn: layout?.spanCol
            ? `span ${Math.max(Math.min(layout.spanCol, 12), 1)}`
            : undefined,
          gridRow: layout?.spanRow
            ? `span ${Math.max(Math.min(layout.spanRow, 12), 1)}`
            : undefined,
          paddingTop: cssOrUndefined(padding?.top),
          paddingRight: cssOrUndefined(padding?.right),
          paddingBottom: cssOrUndefined(padding?.bottom),
          paddingLeft: cssOrUndefined(padding?.left),
          marginTop: cssOrUndefined(margin?.top),
          marginRight: cssOrUndefined(margin?.right),
          marginBottom: cssOrUndefined(margin?.bottom),
          marginLeft: cssOrUndefined(margin?.left),
          flex: layout?.grow === true || layout?.grow === 'true' ? '1 1 0' : undefined,
          // A capped block should fill the space it is allowed and stop at the
          // cap, not shrink to its content — so `Max Width` implies width:100%
          // unless an explicit width was set.
          width:
            cssOrUndefined(layout?.width) ??
            (cssOrUndefined(layout?.blockMaxWidth) ? '100%' : undefined),
          height: cssOrUndefined(layout?.height),
          minWidth: cssOrUndefined(layout?.minWidth),
          minHeight: cssOrUndefined(layout?.minHeight),
          // Caps this block itself (background, border and padding included).
          // Distinct from `maxWidth`, which only caps the inner content via
          // SectionConfigContext so a block can be full-bleed with a narrow
          // column inside it. Left unset, nothing changes.
          maxWidth: cssOrUndefined(layout?.blockMaxWidth),
          alignSelf:
            layout?.alignSelf && layout.alignSelf !== 'auto'
              ? layout.alignSelf
              : undefined,
          justifySelf:
            layout?.alignSelf && layout.alignSelf !== 'auto'
              ? layout.alignSelf
              : undefined,
          ...(layout?.alignSelf === 'center'
            ? { marginLeft: 'auto', marginRight: 'auto' }
            : {}),
          zIndex: layout?.zIndex,
          // Set `Position: Relative` to make this block the containing block for
          // an absolutely-positioned descendant (a Positioner in Absolute mode),
          // so the child anchors here instead of to some ancestor further up.
          //
          // A z-index still implies relative on its own: z-index has no effect on
          // a static element, so setting one and getting nothing would read as a
          // bug. An explicit Position wins over that.
          position:
            layout?.position ||
            (layout?.zIndex != null ? 'relative' : undefined),
          '--mobile-order': layout?.mobileOrder,
          ...style,
        } as React.CSSProperties}
        ref={ref}
      >
        {children}
      </div>
    )
  },
)

Layout.displayName = 'Layout'

export { Layout }

export type WithLayoutOptions = {
  /** Skip padding on Layout wrapper (component renders padding itself). */
  disableWrapperPadding?: boolean
  wrapperStyle?: (props: any) => React.CSSProperties
  /**
   * Skip injecting background/border fields and wrapper styling. Use for
   * components that already handle background/border themselves (e.g. via
   * withStyleFields applying them to their own root element for correct
   * border-radius clipping) so the props aren't rendered twice.
   */
  disableStyleFields?: boolean
}

export function withLayout<
  ThisComponentConfig extends ComponentConfig<any> = ComponentConfig,
>(
  componentConfig: ThisComponentConfig,
  options: WithLayoutOptions = {},
): ThisComponentConfig {
  const wrapperPaddingOverride: React.CSSProperties = options.disableWrapperPadding
    ? {
        paddingTop: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingLeft: 0,
      }
    : {}
  // Heading, Text and Richtext declare their own `shadow` field and apply it to their
  // own element. Injecting a second one here would render the shadow twice, so defer to
  // the component whenever it already owns the prop.
  const ownsShadow = Boolean((componentConfig.fields as Record<string, unknown>)?.shadow)
  const shadowFields = options.disableStyleFields || ownsShadow
    ? {}
    : { shadow: shadowStyleField }
  const styleFields = options.disableStyleFields
    ? {}
    : { background: backgroundStyleField, border: borderStyleField, ...shadowFields }
  const styleFieldDefaults = options.disableStyleFields
    ? {}
    : { background: {}, border: {}, ...(ownsShadow ? {} : { shadow: {} }) }
  return {
    ...componentConfig,
    fields: {
      ...componentConfig.fields,
      layout: layoutField,
      ...styleFields,
    },
    defaultProps: {
      ...styleFieldDefaults,
      ...componentConfig.defaultProps,
      layout: {
        spanCol: 1,
        spanRow: 1,
        padding: defaultSpacing,
        margin: defaultSpacing,
        grow: false,
        maxWidth: '1280px',
        centered: true,
        ...componentConfig.defaultProps?.layout,
      },
    },
    resolveFields: (data: any, params: any) => {
      const innerFields =
        componentConfig.resolveFields
          ? (componentConfig.resolveFields(data as any, params as any) as any)
          : componentConfig.fields
      if (params.parent?.type === 'Grid') {
        return {
          ...innerFields,
          layout: {
            ...layoutField,
            objectFields: {
              anchorId: layoutField.objectFields.anchorId,
              spanCol: layoutField.objectFields.spanCol,
              spanRow: layoutField.objectFields.spanRow,
              padding: layoutField.objectFields.padding,
              margin: layoutField.objectFields.margin,
              mobileOrder: layoutField.objectFields.mobileOrder,
              width: layoutField.objectFields.width,
              height: layoutField.objectFields.height,
              minWidth: layoutField.objectFields.minWidth,
              minHeight: layoutField.objectFields.minHeight,
              blockMaxWidth: layoutField.objectFields.blockMaxWidth,
              maxWidth: layoutField.objectFields.maxWidth,
              position: layoutField.objectFields.position,
              zIndex: layoutField.objectFields.zIndex,
              centered: layoutField.objectFields.centered,
              alignSelf: layoutField.objectFields.alignSelf,
            },
          },
          ...styleFields,
        }
      }
      if (params.parent?.type === 'Flex') {
        return {
          ...innerFields,
          layout: {
            ...layoutField,
            objectFields: {
              anchorId: layoutField.objectFields.anchorId,
              grow: layoutField.objectFields.grow,
              padding: layoutField.objectFields.padding,
              margin: layoutField.objectFields.margin,
              width: layoutField.objectFields.width,
              height: layoutField.objectFields.height,
              minWidth: layoutField.objectFields.minWidth,
              minHeight: layoutField.objectFields.minHeight,
              blockMaxWidth: layoutField.objectFields.blockMaxWidth,
              maxWidth: layoutField.objectFields.maxWidth,
              position: layoutField.objectFields.position,
              zIndex: layoutField.objectFields.zIndex,
              centered: layoutField.objectFields.centered,
              alignSelf: layoutField.objectFields.alignSelf,
            },
          },
          ...styleFields,
        }
      }

      return {
        ...innerFields,
        layout: {
          ...layoutField,
          objectFields: {
            anchorId: layoutField.objectFields.anchorId,
            padding: layoutField.objectFields.padding,
            margin: layoutField.objectFields.margin,
            width: layoutField.objectFields.width,
            height: layoutField.objectFields.height,
            minWidth: layoutField.objectFields.minWidth,
            minHeight: layoutField.objectFields.minHeight,
            blockMaxWidth: layoutField.objectFields.blockMaxWidth,
            maxWidth: layoutField.objectFields.maxWidth,
            position: layoutField.objectFields.position,
            zIndex: layoutField.objectFields.zIndex,
            centered: layoutField.objectFields.centered,
          },
        },
        ...styleFields,
      }
    },
    inline: true,
    render: props => {
      const bgLayers = options.disableStyleFields
        ? { base: {}, overlay: undefined }
        : splitBackgroundLayers(props.background as BackgroundStyleProps)
      const borderStyle = options.disableStyleFields
        ? {}
        : applyBorderStyle(props.border as BorderStyleProps)
      const shadowStyle = options.disableStyleFields || ownsShadow
        ? {}
        : applyShadowStyle(props.shadow as ShadowProps)
      return (
        <SectionConfigContext.Provider
          value={{
            maxWidth: (props.layout as LayoutFieldProps)?.maxWidth ?? '1280px',
            centered: ((v) => v === true || v === 'true')((props.layout as LayoutFieldProps)?.centered ?? true),
          }}
        >
          <Layout
            className={getClassName()}
            layout={props.layout as LayoutFieldProps}
            ref={props.puck.dragRef}
            style={{
              ...bgLayers.base,
              ...borderStyle,
              ...shadowStyle,
              ...wrapperPaddingOverride,
              ...(options.wrapperStyle ? options.wrapperStyle(props) : {}),
              // responsive `display` override (BR-5403); '' = leave as-is
              ...((props as { display?: string }).display
                ? { display: (props as { display?: string }).display }
                : {}),
            }}
          >
            {bgLayers.overlay && <div style={bgLayers.overlay} />}
            {componentConfig.render(props)}
          </Layout>
        </SectionConfigContext.Provider>
      )
    },
  }
}
